import React, { useState, useEffect } from "react";
import { inspectorAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Plus, Edit, Trash2, X, Check, AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Download, Loader } from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { useConfirm } from "../hooks/useConfirm";

const COMPLIANCE_CONFIG = {
  satisfactory:      { label: "Satisfactory",       color: "text-[#637C64] bg-[#EDF2EE]",   border: "border-[#637C64]/30" },
  needs_improvement: { label: "Needs Improvement",  color: "text-[#D98032] bg-[#FDF4EB]",   border: "border-[#D98032]/30" },
  non_compliant:     { label: "Non-Compliant",       color: "text-[#C2624E] bg-[#FBEAE7]",   border: "border-[#C2624E]/30" },
};

const PURPOSES = ["routine", "complaint", "audit", "surprise", "follow-up"];
const DEPARTMENTS = ["State MDM Cell", "Education Department", "Health Department", "MHRD", "FSSAI", "Collector Office", "Other"];

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function InspectorVisits() {
  const { selectedKitchen } = useKitchen();
  const { confirm, ConfirmModal } = useConfirm();
  const [visits, setVisits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [actionItems, setActionItems] = useState([]);
  const [schoolsVisited, setSchoolsVisited] = useState([""]);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => { fetchAll(); }, [selectedKitchen, filterStatus]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [v, s] = await Promise.all([
        inspectorAPI.getVisits({ kitchen_id: selectedKitchen || null, compliance_status: filterStatus || undefined }),
        inspectorAPI.getSummary({ kitchen_id: selectedKitchen || null })
      ]);
      setVisits(v.data || []);
      setSummary(s.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSave = async () => {
    setError("");
    try {
      const payload = {
        ...form,
        kitchen_id: selectedKitchen || null,
        schools_visited: schoolsVisited.filter(s => s.trim()),
        action_items: actionItems.filter(a => a.action?.trim()),
        compliance_status: form.compliance_status || "satisfactory"
      };
      await inspectorAPI.createVisit(payload);
      setModal(null); setForm({}); setActionItems([]); setSchoolsVisited([""]);
      fetchAll();
      setSuccess("Visit logged!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Delete this visit log?")) return;
    await inspectorAPI.deleteVisit(id);
    fetchAll();
  };

  const updateActionStatus = async (visitId, actionIdx, status) => {
    try {
      await inspectorAPI.updateAction(visitId, actionIdx, { status });
      fetchAll();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const cfg = (status) => COMPLIANCE_CONFIG[status] || COMPLIANCE_CONFIG.satisfactory;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="inspector-visits">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Inspector Visit Log</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Government & department inspection records with action tracking</p>
        </div>
        <button onClick={() => { setForm({ visit_date: today, compliance_status: "satisfactory", purpose: "routine", follow_up_required: false }); setActionItems([]); setSchoolsVisited([""]); setModal("create"); setError(""); }}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 font-bold text-sm flex items-center gap-1.5">
          <Plus size={14} /> Log Visit
        </button>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: "Total Visits", value: summary.total_visits, color: "text-[#3A322C]" },
            { label: "Satisfactory", value: summary.satisfactory, color: "text-[#637C64]" },
            { label: "Needs Improvement", value: summary.needs_improvement, color: "text-[#D98032]" },
            { label: "Non-Compliant", value: summary.non_compliant, color: "text-[#C2624E]" },
            { label: "Follow-up Pending", value: summary.follow_up_pending, color: "text-purple-600" },
            { label: "Open Actions", value: summary.open_actions, color: summary.open_actions > 0 ? "text-[#C2624E]" : "text-[#637C64]" },
          ].map(s => (
            <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-3 text-center">
              <p className={`text-xl font-extrabold font-[Manrope] ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-[#8A7E73] mt-0.5 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter + Export */}
      <div className="flex gap-3 flex-wrap items-center">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
          <option value="">All Status</option>
          {Object.entries(COMPLIANCE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => exportCSV(visits.map(v => ({ date: v.visit_date, inspector: v.inspector_name, dept: v.department, purpose: v.purpose, status: v.compliance_status, schools: v.schools_visited?.join(";"), observations: v.observations, actions: v.action_items?.length || 0 })), "inspector_visits")}
          className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
          <Download size={14} /> Export
        </button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <div className="space-y-3">
          {visits.map(visit => {
            const compliance = cfg(visit.compliance_status);
            const isExpanded = expanded === visit.id;
            const openActions = visit.action_items?.filter(a => a.status !== "done").length || 0;
            return (
              <div key={visit.id} className={`bg-white border rounded-xl overflow-hidden ${compliance.border}`}>
                <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#FAFAF8]" onClick={() => setExpanded(isExpanded ? null : visit.id)}>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-bold text-[#3A322C]">{visit.visit_date} — {visit.inspector_name}</p>
                      <p className="text-xs text-[#8A7E73]">{visit.designation} · {visit.department} · {visit.purpose}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {openActions > 0 && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FBEAE7] text-[#C2624E]">{openActions} open action(s)</span>
                    )}
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${compliance.color}`}>{compliance.label}</span>
                    <div className="flex gap-1">
                      <button onClick={e => { e.stopPropagation(); handleDelete(visit.id); }} className="p-1 text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                    </div>
                    {isExpanded ? <ChevronDown size={16} className="text-[#8A7E73]" /> : <ChevronRight size={16} className="text-[#8A7E73]" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-[#F4F3F0] px-5 py-4 space-y-4">
                    {visit.schools_visited?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">Schools Visited</p>
                        <div className="flex flex-wrap gap-1">{visit.schools_visited.map((s, i) => <span key={`idx-${i}`} className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{s}</span>)}</div>
                      </div>
                    )}
                    {visit.observations && (
                      <div>
                        <p className="text-xs font-bold uppercase text-[#8A7E73] mb-1">Observations</p>
                        <p className="text-sm text-[#5C5147] bg-[#FAFAF8] px-4 py-3 rounded-xl">{visit.observations}</p>
                      </div>
                    )}
                    {visit.action_items?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">Action Items</p>
                        <div className="space-y-2">
                          {visit.action_items.map((action, i) => (
                            <div key={`idx-${i}`} className={`flex items-start gap-3 px-4 py-3 rounded-xl ${action.status === "done" ? "bg-[#EDF2EE]" : "bg-[#FDF4EB]"}`}>
                              <button onClick={() => updateActionStatus(visit.id, i, action.status === "done" ? "pending" : "done")}
                                className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${action.status === "done" ? "bg-[#637C64] border-[#637C64]" : "border-[#D98032]"}`}>
                                {action.status === "done" && <Check size={12} className="text-white" />}
                              </button>
                              <div className="flex-1">
                                <p className={`text-sm ${action.status === "done" ? "line-through text-[#8A7E73]" : "text-[#3A322C] font-medium"}`}>{action.action}</p>
                                <p className="text-xs text-[#8A7E73]">{action.assigned_to && `Assigned to: ${action.assigned_to}`} {action.due_date && `· Due: ${action.due_date}`}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {visit.follow_up_date && (
                      <p className="text-xs text-[#D98032]"><strong>Follow-up date:</strong> {visit.follow_up_date}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {visits.length === 0 && (
            <div className="text-center py-16 text-[#8A7E73]">
              <CheckCircle size={48} className="mx-auto mb-3 opacity-20" />
              <p>No inspector visits logged yet</p>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {modal === "create" && (
        <Modal title="Log Inspector Visit" onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Visit Date *</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.visit_date || today} onChange={e => setForm({ ...form, visit_date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Inspector Name *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.inspector_name || ""} onChange={e => setForm({ ...form, inspector_name: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Designation</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. District Education Officer" value={form.designation || ""} onChange={e => setForm({ ...form, designation: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Department</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.department || ""} onChange={e => setForm({ ...form, department: e.target.value })}>
                  <option value="">Select Department</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Purpose</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.purpose || "routine"} onChange={e => setForm({ ...form, purpose: e.target.value })}>
                  {PURPOSES.map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
                </select></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Compliance Status</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.compliance_status || "satisfactory"} onChange={e => setForm({ ...form, compliance_status: e.target.value })}>
                  {Object.entries(COMPLIANCE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">Schools Visited</label>
                <button onClick={() => setSchoolsVisited(prev => [...prev, ""])} className="text-xs text-[#E8956D] font-bold">+ Add</button>
              </div>
              {schoolsVisited.map((s, i) => (
                <div key={`item-${i}`} className="flex gap-2 mb-1.5">
                  <input value={s} onChange={e => { const arr = [...schoolsVisited]; arr[i] = e.target.value; setSchoolsVisited(arr); }} placeholder={`School ${i+1}`} className="flex-1 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
                  {schoolsVisited.length > 1 && <button onClick={() => setSchoolsVisited(prev => prev.filter((_,j) => j !== i))} className="text-[#C4BAAF] hover:text-[#C2624E]"><X size={15} /></button>}
                </div>
              ))}
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Observations / Findings</label><textarea rows={3} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.observations || ""} onChange={e => setForm({ ...form, observations: e.target.value })} /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">Action Items</label>
                <button onClick={() => setActionItems(prev => [...prev, { action: "", assigned_to: "", due_date: "", status: "pending" }])} className="text-xs text-[#E8956D] font-bold">+ Add Action</button>
              </div>
              {actionItems.map((a, i) => (
                <div key={`item-${i}`} className="grid grid-cols-[1fr_120px_100px_24px] gap-2 mb-2 items-center">
                  <input placeholder="Action required" value={a.action} onChange={e => { const arr = [...actionItems]; arr[i].action = e.target.value; setActionItems(arr); }} className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D]" />
                  <input placeholder="Assigned to" value={a.assigned_to} onChange={e => { const arr = [...actionItems]; arr[i].assigned_to = e.target.value; setActionItems(arr); }} className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D]" />
                  <input type="date" value={a.due_date} onChange={e => { const arr = [...actionItems]; arr[i].due_date = e.target.value; setActionItems(arr); }} className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D]" />
                  <button onClick={() => setActionItems(prev => prev.filter((_,j) => j !== i))} className="text-[#C4BAAF] hover:text-[#C2624E]"><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.follow_up_required || false} onChange={e => setForm({ ...form, follow_up_required: e.target.checked })} />
                <label className="text-sm text-[#3A322C]">Follow-up required</label>
              </div>
              {form.follow_up_required && (
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Follow-up Date</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.follow_up_date || ""} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} /></div>
              )}
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Visit Log</button>
            </div>
          </div>
        </Modal>
      )}
      {ConfirmModal}
    </div>
  );
}
